'use client';

import { GroqImage } from '@/helpers/Image';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/useIsMobile';
import styles from './WhatHappensNow.module.scss';

interface IWhatHappensNowProps {
  title: PortableTextBlock[];
  button: GROQButton;
  buttonMobile: GROQButton;
  cards: {
    icon: GROQImage;
    text: PortableTextBlock[];
  }[];
}

export default function WhatHappensNow({
  title,
  button,
  buttonMobile,
  cards,
}: IWhatHappensNowProps) {
  const isMobile = useIsMobile();

  return (
    <section className="pt-[3rem] lg:py-[12rem]">
      <div className="container ">
        <div className="flex flex-col gap-[5rem] lg:gap-[9.5rem]">
          <div className="w-full flex items-start lg:items-center lg:flex-row flex-col justify-between">
            <div className="flex flex-col gap-[2.5rem] text-[#dadada] uppercase prose-p:text-[#f2f0ef] prose-p:text-[1.625rem] prose-p:tracking-[0.53625rem] prose-p:opacity-70">
              {title && (
                <PortableText value={title} components={richTextComponent} />
              )}
            </div>

            <div className="lg:block hidden">{button && <CTAButton data={button} />}</div>
          </div>

          <ul
            style={{
              height: isMobile ? 'auto' : `${(cards?.length || 0) * 25}vh`,
            }}
            className={`flex lg:flex-col flex-col items-stretch gap-[4rem] lg:gap-[2rem] relative `}
          >
            {cards?.map((item, index) => {
              return (
                <li
                  key={index}
                  className={`lg:top-[10rem] lg:sticky group`}
                  style={{
                    marginLeft: !isMobile ? `${index * 25}%` : '0',
                  }}
                >
                  <div className="lg:min-h-[31rem] p-[3rem] lg:p-[2.6rem] rounded-[0.5rem] border-[.125rem] border-white/[0.08] duration-500 ease-out hover:bg-[rgba(255,255,255,0.08)] bg-black lg:w-[54rem] flex flex-col gap-[4rem] lg:gap-[5rem] text-[#f2f0ef] prose-p:font-[300] prose-p:opacity-70">
                    <div className="flex flex-col gap-[3rem] items-start">
                      <div className="w-[5rem] h-[5rem]">
                        {item?.icon && (
                          <GroqImage
                            alt="Icon"
                            className="!w-full !h-full !object-cover"
                            src={item?.icon}
                          />
                        )}
                      </div>

                      <span className="block font-nunito text-[3rem] text-white font-[300]">
                        {index + 1 < 10 ? `0${index + 1}` : index + 1}
                      </span>
                    </div>

                    {item?.text && (
                      <PortableText
                        value={item?.text}
                        components={richTextComponent}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {buttonMobile &&
            <div className="lg:hidden">
              <CTAButton data={buttonMobile} />
            </div>
          }
        </div>
      </div>
    </section>
  );
}
